import React, { FC, useState } from 'react';
import { useHistory } from 'react-router-dom';
import apolloClient from '@src/graphql/apollo';
import ActionModal from '@src/views/FunnelOverview/ActionModal';
import { closeModal, setModalContent } from '@src/redux/actions/modal';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { FUNNEL_EDIT_ROUTE, FUNNEL_EXPORT_ROUTE } from '@src/Routes/links';
import { ARCHIVE_OPENING, UPDATE_OPENING_STATE } from '@src/constants';
import { trackEvent, trackViewCreateTargeting } from '@src/metrics';
import PauseSvg from '@src/svgs/fr-pause.svg';
import { checkSubscriptionState } from '@src/redux/actions/subscription';
import { checkOpeningLimit } from '@src/redux/actions/opening';
import { Appearance } from '@src/components/Button';
import { sendNewIntercomMessage } from '@src/intercom';
import PlaySvg from '@src/svgs/fr-play.svg';
import { RepublishOpeningModal } from '@src/containers/SubscriptionModals/RepublishOpening';
import {
  DuplicateOpeningDocument,
  ResumeOpeningDocument,
  ArchiveOpeningDocument,
  OpeningSourceEnum,
  OpeningStateEnum,
} from '@src/graphql/generated';
import { useAppDispatch } from '@src/redux';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { EditPitchModal } from '@src/containers/EditPitchModal';
import { createLink } from '@src/util/createLink';
import { isActive, isAdminReview, isDraft, isInactive, isPaused } from '@src/util/opening';
import { Action } from './interfaces/action.interface';
import { OpeningActionsProps } from './props';
import './styles.scss';

export const OpeningActions: FC<OpeningActionsProps> = ({
  actionSelection = ['duplicate', 'pause', 'stop', 'edit', 'editPitch', 'export', 'activate', 'archive'],
  children,
  metricsPrevContext,
  opening,
  updateOpening,
}) => {
  const history = useHistory();

  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const stopOpening = async (): Promise<void> => {
    const openingId = opening.id;

    trackEvent({
      context: MetricContext.DEACTIVATE_OPENING,
      interaction: MetricAction.OPEN_DEACTIVATE_OPENING,
      payload: {
        'Deactivation Type': 'Stop',
        'Event Source':
          metricsPrevContext === MetricContext.OPENINGS_LIST
            ? MetricEventSource.STOP_OPENING_ACTION
            : MetricEventSource.STOP_OPENING_BUTTON,
        'Opening Id': openingId,
        'Previous Context': metricsPrevContext,
      },
    });

    dispatch(
      setModalContent({
        className: 'opening-modal-wrapper',
        content: openingId && (
          <ActionModal
            onError={() => dispatch(setErrorFlashMessage())}
            onSuccess={opening => {
              updateOpening(opening, UPDATE_OPENING_STATE);
              dispatch(setSuccessFlashMessage(`The opening ${opening.title} is stopped`));
            }}
            openingId={openingId}
            position={opening.title}
            type="stop"
          />
        ),
      }),
    );
  };

  const duplicateOpening = async (): Promise<void> => {
    try {
      const {
        data: { duplicateOpening: newOpening },
      } = await apolloClient.mutate({
        fetchPolicy: 'no-cache',
        mutation: DuplicateOpeningDocument,
        variables: { openingId: opening.id },
      });

      history.push(createLink({ link: FUNNEL_EDIT_ROUTE, params: { openingId: newOpening.id } }));

      dispatch(setSuccessFlashMessage(`The opening ${newOpening.title} is successfully duplicated`));

      trackViewCreateTargeting({
        'Event Source': MetricEventSource.DUPLICATE_OPENING_ACTION,
        'Previous Context': metricsPrevContext,
      });
    } catch (error) {
      dispatch(setErrorFlashMessage());
      setIsLoading(false);
    }
  };

  const editOpening = (): void => {
    if (opening.source === OpeningSourceEnum.SCRAPER) {
      // If the opening is scraped we duplicate it. This will pause the existing
      // opening and create a new "normal" one on the backend which can be edited.
      duplicateOpening();

      return;
    }

    if (isActive(opening.state) || isPaused(opening.state)) {
      sendNewIntercomMessage(
        `Live editing is not available for an active opening since it has already been distributed as a direct push notification to suitable talents. Please send us a message with requested changes for "${opening.title}" – we will swiftly implement them for you.`,
      );

      return;
    }

    if (isDraft(opening.state)) {
      history.push(
        createLink({
          link: FUNNEL_EDIT_ROUTE,
          params: { openingId: opening.id },
        }),
      );
    }
  };

  const pauseOpening = async (): Promise<void> => {
    const openingId = opening.id;

    trackEvent({
      context: MetricContext.DEACTIVATE_OPENING,
      interaction: MetricAction.OPEN_DEACTIVATE_OPENING,
      payload: {
        'Deactivation Type': 'Pause',
        'Event Source':
          metricsPrevContext === MetricContext.OPENINGS_LIST
            ? MetricEventSource.PAUSE_OPENING_ACTION
            : MetricEventSource.PAUSE_OPENING_BUTTON,
        'Opening Id': openingId,
        'Previous Context': metricsPrevContext,
      },
    });

    dispatch(
      setModalContent({
        className: 'opening-modal-wrapper',
        content: openingId && (
          <ActionModal
            onError={() => dispatch(setErrorFlashMessage())}
            onSuccess={opening => {
              updateOpening(opening, UPDATE_OPENING_STATE);
              dispatch(setSuccessFlashMessage(`The opening ${opening.title} is paused`));
            }}
            openingId={openingId}
            position={opening.title}
            type="pause"
          />
        ),
      }),
    );
  };

  const resumeOpening = async (): Promise<void> => {
    try {
      const openingId = opening.id;
      const {
        data: { resumeOpening: resumedOpening },
      } = await apolloClient.mutate({
        fetchPolicy: 'no-cache',
        mutation: ResumeOpeningDocument,
        variables: { openingId: openingId },
      });

      updateOpening(resumedOpening, UPDATE_OPENING_STATE);

      dispatch(setSuccessFlashMessage(`The opening ${resumedOpening.title} is activated`));

      trackEvent({
        context: MetricContext.OPENING_ACTIONS,
        interaction: MetricAction.ACTIVATE_OPENING,
        payload: {
          'Event Source':
            metricsPrevContext === MetricContext.OPENINGS_LIST
              ? MetricEventSource.ACTIVATE_OPENING_ACTION
              : MetricEventSource.ACTIVATE_OPENING_BUTTON,
          'Opening Id': openingId,
          'Previous Context': metricsPrevContext,
        },
      });
    } catch (error) {
      dispatch(setErrorFlashMessage());
      setIsLoading(false);
    }
  };

  const exportCandidates = async (): Promise<void> => {
    const openingId = opening.id;
    const exportLink = createLink({ link: FUNNEL_EXPORT_ROUTE, params: { openingId } });

    window.open(`/employer${exportLink}`, '_blank');

    trackEvent({
      context: MetricContext.OPENING_ACTIONS,
      interaction: MetricAction.EXPORT_CSV,
      payload: {
        'Event Source': MetricEventSource.EXPORT_OPENING_CSV_ACTION,
        'Opening Id': openingId,
        'Previous Context': metricsPrevContext,
      },
    });
  };

  const archiveOpening = async (): Promise<void> => {
    const openingId = opening.id;

    try {
      const {
        data: { archiveOpening: archiveddOpening },
      } = await apolloClient.mutate({
        fetchPolicy: 'no-cache',
        mutation: ArchiveOpeningDocument,
        variables: { openingId: openingId },
      });

      updateOpening({ ...opening, ...archiveddOpening }, ARCHIVE_OPENING);

      dispatch(setSuccessFlashMessage(`The opening ${opening.title} is archived`));
    } catch (error) {
      dispatch(setErrorFlashMessage());
      setIsLoading(false);
    }

    trackEvent({
      context: MetricContext.OPENING_ACTIONS,
      interaction: MetricAction.ARCHIVE_OPENING,
      payload: {
        'Event Source': MetricEventSource.ARCHIVE_OPENING_ACTION,
        'Opening Id': openingId,
        'Previous Context': metricsPrevContext,
      },
    });
  };

  const activateOpening = async (): Promise<void> => {
    // In case the opening got scraped we do not ask to increase
    // the slots count
    if (opening.source === OpeningSourceEnum.SCRAPER || opening.source?.type === OpeningSourceEnum.SCRAPER) {
      resumeOpening();

      return;
    }

    // check if user has not reached active opening limit
    const handleOpeningLimitCheck = (): void => {
      dispatch(
        checkOpeningLimit({
          nextFunction: resumeOpening,
        }),
      );
    };

    dispatch(
      checkSubscriptionState({
        eventSource:
          metricsPrevContext === MetricContext.OPENINGS_LIST
            ? MetricEventSource.ACTIVATE_OPENING_ACTION
            : MetricEventSource.ACTIVATE_OPENING_BUTTON,
        isNetworkOnly: true,
        metricsPrevContext: metricsPrevContext,
        nextFunction: handleOpeningLimitCheck,
        onCompleted: handleOpeningLimitCheck,
      }),
    );
  };

  const republishOpening = async (): Promise<void> => {
    dispatch(
      setModalContent({
        content: (
          <RepublishOpeningModal
            onUpdateOpening={() => updateOpening({ ...opening, state: OpeningStateEnum.ACTIVE }, UPDATE_OPENING_STATE)}
            openingId={opening.id}
          />
        ),
      }),
    );
  };

  const editPitch = () => {
    if (opening.id) {
      dispatch(
        setModalContent({
          content: <EditPitchModal onClose={() => dispatch(closeModal())} openingId={opening.id} />,
        }),
      );
    }
  };

  const handleAction = async ({ key }: Action): Promise<void> => {
    const methods = {
      activate: activateOpening,
      archive: archiveOpening,
      duplicate: duplicateOpening,
      edit: editOpening,
      editPitch: editPitch,
      export: exportCandidates,
      pause: pauseOpening,
      republish: republishOpening,
      stop: stopOpening,
    };

    if (!methods[key]) {
      throw new Error('Action not found in a list of opening actions');
    }

    setIsLoading(true);

    await methods[key]();

    setIsLoading(false);
  };

  const isScrapedOpening = opening.source === OpeningSourceEnum.SCRAPER;

  const canStop = !isLoading && (isActive(opening.state) || isAdminReview(opening.state) || isPaused(opening.state));
  const canArchive =
    !isLoading && !isScrapedOpening && (isDraft(opening.state) || isPaused(opening.state) || isInactive(opening.state));
  const canPause = !isLoading && isActive(opening.state);
  const canActivate = !isLoading && isPaused(opening.state);
  const canRepublish = !isLoading && !isScrapedOpening && isActive(opening.state);
  const canEdit = !isLoading && (isActive(opening.state) || isDraft(opening.state) || isPaused(opening.state));
  const canExportCsv = !isLoading && !isScrapedOpening;
  const canDuplicate = !isLoading && !isScrapedOpening;

  const actions = [
    {
      appearance: Appearance.PRIMARY,
      enabled: canActivate,
      icon: PlaySvg,
      key: 'activate',
      label: 'Activate',
    },
    {
      appearance: Appearance.SECONDARY,
      enabled: canPause,
      icon: PauseSvg,
      key: 'pause',
      label: 'Pause',
    },
    {
      appearance: Appearance.PRIMARY,
      enabled: canStop,
      key: 'stop',
      label: 'Stop',
    },
    {
      appearance: Appearance.INVISIBLE,
      enabled: canRepublish,
      key: 'republish',
      label: 'Republish',
    },
    {
      appearance: Appearance.PRIMARY,
      enabled: canEdit,
      key: 'edit',
      label: 'Edit',
    },
    {
      appearance: Appearance.PRIMARY,
      enabled: true,
      key: 'editPitch',
      label: 'Edit pitch',
    },
    {
      enabled: canDuplicate,
      key: 'duplicate',
      label: 'Duplicate',
    },
    {
      enabled: canExportCsv,
      key: 'export',
      label: 'Export CSV',
    },
    {
      enabled: canArchive,
      key: 'archive',
      label: 'Archive',
    },
  ].filter(({ enabled: isEnabled, key }) => isEnabled && actionSelection.includes(key));

  return children({
    actions: actions,
    handleAction: handleAction,
  });
};
